// What the composer keeps per signed-in user in this browser (localStorage): the open support
// conversation, so a new tab or a later visit finds it again and staff replies aren't missed (CH-3).
// Also, for whoever signs in, the conversation a support reply email's link asked for. Kept apart from
// the composer so the dock can clear it without loading the composer.

const CONVERSATION_KEY = '@LT/SUPPORT_CONVERSATION';
const LAST_USER_KEY = '@LT/SUPPORT_LAST_USER';
// Set once this tab has asked the server for the customer's conversations (resumeFromServer).
const RESUME_CHECKED_KEY = '@LT/SUPPORT_RESUME_CHECKED';
// The conversation the customer left ("New conversation", or gone): never picked up from the server again.
const LEFT_KEY = '@LT/SUPPORT_LEFT';
// Not per user: the link is often opened signed out, and must outlast a sign-in that clears those.
const PENDING_KEY = '@LT/SUPPORT_LINK';
// A signed-out visitor's conversation (localStorage, for 7 days) and what they typed to start it
// (sessionStorage, gone when the browser session ends). Both go when a customer signs in here.
const VISITOR_KEY = '@LT/SUPPORT_VISITOR';
const VISITOR_DETAILS_KEY = '@LT/SUPPORT_VISITOR_DETAILS';
export const VISITOR_CONVERSATION_DAYS = 7;
const VISITOR_CONVERSATION_MS = VISITOR_CONVERSATION_DAYS * 24 * 60 * 60 * 1000;

export function readStorage(storage, key) {
  try {
    return window[storage].getItem(key);
  } catch (e) {
    return null;
  }
}

export function writeStorage(storage, key, value) {
  try {
    if (value === null) {
      window[storage].removeItem(key);
    } else {
      window[storage].setItem(key, value);
    }
  } catch (e) {
    // Private mode: the composer still works, it just forgets between reloads.
  }
}

function userKey(prefix, userId) {
  return `${prefix}:${userId || 'user'}`;
}

export function readConversation(userId) {
  const key = userKey(CONVERSATION_KEY, userId);
  const stored = readStorage('localStorage', key);
  if (stored) return stored;
  // Until 4 Nov 2026 the id was kept per tab (sessionStorage): move it to this browser once.
  const tab = readStorage('sessionStorage', key);
  if (tab) {
    writeStorage('localStorage', key, tab);
    writeStorage('sessionStorage', key, null);
  }
  return tab;
}

export function writeConversation(userId, conversationId) {
  const key = userKey(CONVERSATION_KEY, userId);
  writeStorage('localStorage', key, conversationId || null);
  writeStorage('sessionStorage', key, null);
}

/** The conversation this customer left in this browser, so resumeFromServer doesn't bring it back. */
export function readLeftConversation(userId) {
  return readStorage('localStorage', userKey(LEFT_KEY, userId));
}

export function writeLeftConversation(userId, conversationId) {
  writeStorage('localStorage', userKey(LEFT_KEY, userId), conversationId || null);
}

/** Whether this tab already asked the server for this customer's conversations. */
export function readResumeChecked(userId) {
  return readStorage('sessionStorage', userKey(RESUME_CHECKED_KEY, userId)) === '1';
}

export function writeResumeChecked(userId) {
  writeStorage('sessionStorage', userKey(RESUME_CHECKED_KEY, userId), '1');
}

/** The conversation id from a support reply email's link (deepLink.js), until a composer opens it. */
export function readPendingConversation() {
  return readStorage('sessionStorage', PENDING_KEY);
}

export function writePendingConversation(conversationId) {
  writeStorage('sessionStorage', PENDING_KEY, conversationId || null);
}

function readJson(storage, key) {
  const raw = readStorage(storage, key);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : null;
  } catch (e) {
    // Not ours, or damaged: as if there were none.
    return null;
  }
}

function isText(value) {
  return typeof value === 'string' && !!value;
}

/**
 * The conversation a signed-out visitor started in this browser: {conversationId, token}, or null for
 * none. The token is the only way back into it; the server returned it once, when the conversation was
 * created. Kept for 7 days from the last message sent with it: an older one (or anything unreadable)
 * is removed here, so a shared browser doesn't hand it to the next person for long.
 */
export function readVisitorConversation(now = Date.now()) {
  const value = readJson('localStorage', VISITOR_KEY);
  const age = value ? now - value.savedAt : NaN;
  if (
    value &&
    isText(value.conversationId) &&
    isText(value.token) &&
    typeof value.savedAt === 'number' &&
    age >= 0 &&
    age < VISITOR_CONVERSATION_MS
  ) {
    return { conversationId: value.conversationId, token: value.token };
  }
  if (readStorage('localStorage', VISITOR_KEY) !== null) {
    writeStorage('localStorage', VISITOR_KEY, null);
  }
  return null;
}

/** Keeps the conversation and its token, from now; without both, forgets it. */
export function writeVisitorConversation(record, now = Date.now()) {
  writeStorage(
    'localStorage',
    VISITOR_KEY,
    record && isText(record.conversationId) && isText(record.token)
      ? JSON.stringify({
          conversationId: record.conversationId,
          token: record.token,
          savedAt: now,
        })
      : null
  );
}

/** Forgets the conversation and its token; the details stay for the next one in this session. */
export function clearVisitorConversation() {
  writeStorage('localStorage', VISITOR_KEY, null);
}

/** The visitor's name, email and company in this browser session ('' for any not given). */
export function readVisitorDetails() {
  const value = readJson('sessionStorage', VISITOR_DETAILS_KEY);
  const text = field =>
    value && typeof value[field] === 'string' ? value[field] : '';
  return { name: text('name'), email: text('email'), company: text('company') };
}

export function writeVisitorDetails(details) {
  const value = {
    name: (details && details.name) || '',
    email: (details && details.email) || '',
    company: (details && details.company) || '',
  };
  writeStorage(
    'sessionStorage',
    VISITOR_DETAILS_KEY,
    value.name || value.email || value.company ? JSON.stringify(value) : null
  );
}

/** Everything a visitor left in this browser: on "Forget this chat", and when a customer signs in. */
export function forgetVisitor() {
  writeStorage('localStorage', VISITOR_KEY, null);
  writeStorage('sessionStorage', VISITOR_DETAILS_KEY, null);
}

// Every key in a Storage (key()/length); stand-ins without them keep keys as own properties.
function storedKeys(store) {
  if (typeof store.key === 'function' && typeof store.length === 'number') {
    const keys = [];
    for (let i = 0; i < store.length; i++) keys.push(store.key(i));
    return keys;
  }
  return Object.keys(store);
}

// Conversation ids of every user in this browser (and the per-tab ones kept before 4 Nov 2026).
export function clearStoredConversations() {
  ['localStorage', 'sessionStorage'].forEach(storage => {
    try {
      const store = window[storage];
      storedKeys(store)
        .filter(
          key =>
            key &&
            (key.indexOf(CONVERSATION_KEY) === 0 || key.indexOf(LEFT_KEY) === 0)
        )
        .forEach(key => store.removeItem(key));
    } catch (e) {
      // Storage unavailable: nothing was stored.
    }
  });
}

/**
 * Call when a customer is signed in. Signing out keeps what is stored (it is per user, so the same
 * customer finds their conversation again); a different customer signing in to this browser clears
 * it, so a shared computer never keeps the last one's conversation (their own comes back from the
 * server when they sign in again).
 */
export function noteSignedInUser(userId) {
  if (!userId) return;
  const current = String(userId);
  const last =
    readStorage('localStorage', LAST_USER_KEY) ||
    readStorage('sessionStorage', LAST_USER_KEY);
  if (last && last !== current) clearStoredConversations();
  writeStorage('localStorage', LAST_USER_KEY, current);
  writeStorage('sessionStorage', LAST_USER_KEY, null);
}



// WEBPACK FOOTER //
// ./src/support/conversationStore.js